import React, { useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Row, Badge, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import BackToListing from '@components/BackToListing';

import {
  CalendarOutlined,
  DashboardOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  ProfileOutlined,
  UserOutlined,
} from '@ant-design/icons';
import StatCard from '@components/StatCard';

import useSwr from '@hooks/useSwr';
import { BiBlock } from 'react-icons/bi';
import { IoSpeedometerOutline } from 'react-icons/io5';
import SessionContext from '@providers/Session/SessionContext';
import { bytesFormat, formatCurrency } from '@utils/formatters';
import CustomTag from '@components/CustomTag';
import { AddressType, ContractDetailsType } from '../types';

const DetailedContractCustomer = () => {
  const { t } = useTranslation(['customer-contracts', 'inventory', 'status']);
  const { theme } = useContext(SessionContext);

  const { assetNumber } = useParams();
  const { data } = useSwr<ContractDetailsType>(
    '/service-proxy/contracts/info',
    {
      params: {
        assetId: assetNumber,
      },
    },
  );

  const formatAddressValue = useCallback(
    (address: AddressType) =>
      `${address?.street?.name} ${address?.street?.addressNumber}, ${address?.street?.neighbourhood?.name}, ${address?.city?.name}-${address?.state?.code}`,
    [],
  );

  const objWithAllItems = [
    {
      title: t('details-block.amountContracted'),
      value: data?.amountContracted,
      icon: <ProfileOutlined />,
    },
    {
      title: t('inventory:table.nickname'),
      value: data?.nickname,
      icon: <UserOutlined />,
    },
    {
      title: t('details-block.billingAccountEmail'),
      value: data?.billingAccountEmail,
      icon: <MailOutlined />,
    },
    {
      title: t('details-block.isca'),
      value: data?.relatedProduct.find(item => item.name === 'Isca')?.value,
      icon: <FileTextOutlined />,
    },
    {
      title: t('details-block.billingAccountNumber'),
      visible:
        data?.billingAccountNumber && data?.billingAccountNumber.length !== 0,
      value: `${data?.billingAccountNumber} `,
      icon: <ProfileOutlined />,
    },
    {
      title: t('details-block.directDebit'),
      visible: data?.directDebit,
      value:
        data?.directDebit === 'N'
          ? t('details-block.directDebitValues.without-debit')
          : t('details-block.directDebitValues.with-debit'),
      icon: (
        <Badge
          color={
            data?.directDebit === 'N'
              ? theme?.colors?.green
              : theme?.colors?.red
          }
        />
      ),
    },
    {
      title: t('details-block.lineOfBusiness'),
      value: data?.lineOfBusiness,
      icon: <PhoneOutlined />,
    },
    {
      title: t('details-block.partiallyBlocked'),
      visible: data?.partiallyBlocked,
      value:
        data?.partiallyBlocked === 'N'
          ? t('details-block.partiallyBlockedValues.without-blocked')
          : t('details-block.partiallyBlockedValues.partially-blocked'),
      icon: <BiBlock />,
    },
    {
      title: t('details-block.paymentMode'),
      value: data?.paymentMode,
      icon: <ProfileOutlined />,
    },
    {
      title: t('details-block.shippingDate'),
      value: data?.shippingDate.split(' ')[0],
      icon: <CalendarOutlined />,
    },
    {
      title: t('details-block.activationDate'),
      value: data?.activationDate.split(' ')[0],
      icon: <CalendarOutlined />,
    },
    {
      title: t('details-block.disconnectDate'),
      value: data?.disconnectDate.split(' ')[0],
      icon: <CalendarOutlined />,
    },
    {
      title: t('details-block.speed'),
      value: data?.speed,
      icon: <IoSpeedometerOutline />,
    },
    {
      title: `${t('details-block.consumption')} / ${t(
        'details-block.franchise',
      )}`,
      value: `${bytesFormat(data?.consumption)} / ${bytesFormat(
        data?.franchiseTotal,
      )}`,
      icon: <IoSpeedometerOutline />,
    },
    {
      title: t('table.limiter.label'),
      value: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: -4,
          }}
        >
          <CustomTag color={data?.limiter ? 'green' : 'red'}>
            {t(`table.limiter.${data?.limiter ? 'active' : 'inactive'}`)}
          </CustomTag>
          {bytesFormat(data?.limitingValue)}
        </div>
      ),
      visible: !!data?.limiter,
      icon: <DashboardOutlined />,
    },
    {
      title: t('table.limiter.costOfExcessMegabytes'),
      value: formatCurrency(data?.costOfExcessMegabytes ?? 0),
      visible: !!data?.limiter,
      icon: <DollarOutlined />,
    },
  ];

  return (
    <div>
      <BackToListing
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {` ${t('details-title')} - ${assetNumber || ''}`}
          </div>
        }
      />
      <Row gutter={[8, 8]}>
        <Skeleton loading={!data}>
          {objWithAllItems?.map(
            ({ visible, title, icon, value }) =>
              (visible ?? value) && (
                <Col key={title} xl={8} lg={12} xs={24}>
                  {/* @ts-ignore */}
                  <StatCard title={title} icon={icon} value={value ?? ''} />
                </Col>
              ),
          )}

          {data?.address.map(item => (
            <Col key={item.type} span={24}>
              <StatCard
                title={t('details-block.address', {
                  type: item.type,
                })}
                icon={<EnvironmentOutlined />}
                value={formatAddressValue(item)}
              />
            </Col>
          ))}
        </Skeleton>
      </Row>
    </div>
  );
};

export default DetailedContractCustomer;
